import { useState, useEffect, useMemo, useCallback } from 'react';
import { FileText, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/ui/Pagination';

const ITEMS_PER_PAGE = 10;

export default function ClientInvoices() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return invoices.slice(start, start + ITEMS_PER_PAGE);
  }, [invoices, currentPage]);

  const totalPages = Math.ceil(invoices.length / ITEMS_PER_PAGE);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/my-invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setInvoices(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/payments/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Mes factures</h1>
          <p className="text-muted-foreground">Consultez et téléchargez vos factures</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <img src="/images/facturation-electronique.png" alt="Facturation électronique" className="h-12 w-auto" />
          <p className="text-xs text-muted-foreground max-w-[200px] leading-tight">Nos factures sont conformes à la réforme sur la facturation électronique</p>
        </div>
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune facture</h3>
            <p className="text-muted-foreground">
              Vos factures apparaîtront ici après vos paiements.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-4 font-medium text-muted-foreground">N° Facture</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Montant</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4 font-medium">{invoice.invoice_number}</td>
                      <td className="p-4 text-muted-foreground">
                        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          invoice.type === 'subscription' 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : invoice.type === 'manual'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        }`}>
                          {invoice.type === 'subscription' ? 'Abonnement' : invoice.type === 'manual' ? 'Manuel' : 'Devis'}
                        </span>
                      </td>
                      <td className="p-4 text-right font-semibold">{invoice.amount}€</td>
                      <td className="p-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                          disabled={downloading === invoice.id}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {downloading === invoice.id ? 'Téléchargement...' : 'PDF'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={invoices.length}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
